// src/auth/createAuthedAxios.ts
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import  store  from "../store";
import { login, logout } from "../actions/userActions";


type TokenManager = {
  get: () => string | null;           // read current access token (from Redux or state)
  set: (t: string | null) => void;    // update access token after refresh
  onUnauthorized?: () => void;        // optional: e.g., dispatch(logout)
};

export function createAuthedAxios(
  tm: TokenManager,
  opts?: { baseURL?: string }
): AxiosInstance {
  const api = axios.create({
    baseURL: opts?.baseURL ?? "/api",  // adjust to your API base
    // normal requests don't need cookies; refresh call will set withCredentials itself
  });

  // Attach Authorization automatically
  api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = tm.get();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });

  // Single-flight refresh gate (avoid many parallel refreshes)
  let isRefreshing = false;
  let waiters: ((t: string | null) => void)[] = [];
  const notifyAll = (t: string | null) => { waiters.forEach(fn => fn(t)); waiters = []; };
  const onToken = (fn: (t: string | null) => void) => waiters.push(fn);

  // Call refresh endpoint (cookie is sent here)
  const refreshAccess = async (): Promise<string> => {
    const res = await axios.post<{ access_token: string }>(
      `${api.defaults.baseURL}/auth/refresh`,
      null,
      { withCredentials: true }            // send HttpOnly refresh cookie
    );
    const newToken = res.data.access_token;
    tm.set(newToken);
    return newToken;
  };

  api.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const { response } = error;
      const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

      // Not a 401 or no original config → bubble up
      if (!original || response?.status !== 401) {
        if (response?.status === 401 && tm.onUnauthorized) tm.onUnauthorized();
        return Promise.reject(error);
      }
      if (original._retry) {
        // Already retried once → give up
        if (tm.onUnauthorized) tm.onUnauthorized();
        return Promise.reject(error);
      }
      original._retry = true;

      // Ensure only one refresh happens; others wait
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccess();
          isRefreshing = false;
          notifyAll(newToken);
        } catch (e) {
          isRefreshing = false;
          tm.set(null);
          notifyAll(null);
          if (tm.onUnauthorized) tm.onUnauthorized();
          return Promise.reject(e);
        }
      }

      // Wait for refresh result, then retry once with new token
      return new Promise((resolve, reject) => {
        onToken((t) => {
          if (!t) return reject(error);
          original.headers = original.headers ?? {};
          original.headers["Authorization"] = `Bearer ${t}`;
          resolve(api(original));
        });
      });
    }
  );

  return api;
}


export const axiosAuthed = createAuthedAxios(
    {
        
      get: () => store.getState().userLogin.todoUserInfo?.access_token ?? null,
      set: (t) => store.dispatch(login(t)),  // update Redux
      onUnauthorized: () => store.dispatch(logout()), // optional
    },
    { baseURL: "/api" }  // or "https://api.example.com"
  );